// mock-only: replay controller for committed analytics frames (ADR-0013).

import { useCallback, useEffect, useRef, useState } from 'react';

import {
  loadFixtureRequest,
  loadFixtureReplay,
  replayAnalysis,
  type AnalysisReplayRun,
} from '@/shared/contracts/analytics/client';
import type { AnalysisReplay } from '@/shared/contracts/analytics/v1';
import {
  recordedPresentation,
  upgradePresentation,
  type ReplayPresentation,
} from '@/versions/v1/shared/analysis-replay-state';

type ReplayLoadState =
  | { status: 'idle' }
  | { status: 'loading'; fixture: string }
  | { status: 'failed'; fixture: string }
  | { status: 'ready'; fixture: string; presentation: ReplayPresentation };

const inFlightReplayRequests = new Map<string, Promise<AnalysisReplayRun>>();

/** One replay per case fixture. A committed recording is visible while the service evaluates. */
export function useAnalysisReplays(fixtures: readonly string[]): Readonly<Record<string, AnalysisReplay>> {
  const key = [...new Set(fixtures)].sort().join('|');
  const [frames, setFrames] = useState<Record<string, AnalysisReplay>>({});
  useEffect(() => {
    let cancelled = false;
    const live = new Set<string>();
    setFrames({});
    for (const fixture of key.split('|').filter(Boolean)) {
      const publish = (value: AnalysisReplay) => {
        if (!cancelled) setFrames((current) => ({ ...current, [fixture]: value }));
      };
      void loadFixtureReplay(fixture).then((value) => {
        if (!live.has(fixture)) publish(value);
      }).catch(() => undefined);
      void replayFixture(fixture).then((value) => {
        if (value.source === 'live') live.add(fixture);
        if (value.source === 'live') publish(value.frames);
      }).catch(() => undefined);
    }
    return () => { cancelled = true; };
  }, [key]);
  return frames;
}

function replayFixture(fixture: string): Promise<AnalysisReplayRun> {
  const pending = inFlightReplayRequests.get(fixture);
  if (pending) return pending;

  const replay = loadFixtureRequest(fixture).then((request) => replayAnalysis(request, { fixture }));
  inFlightReplayRequests.set(fixture, replay);
  const clear = () => {
    if (inFlightReplayRequests.get(fixture) === replay) inFlightReplayRequests.delete(fixture);
  };
  void replay.then(clear, clear);
  return replay;
}

export type AnalysisReplayController =
  | { status: 'idle' | 'loading' | 'failed' }
  | {
      status: 'ready';
      frames: AnalysisReplay;
      source: 'live' | 'fixture';
      frameIndex: number;
      frame: AnalysisReplay[number];
      playing: boolean;
      framesPerSecond: 1 | 4 | 16;
      selectFrame: (index: number) => void;
      togglePlayback: () => void;
      setFramesPerSecond: (speed: 1 | 4 | 16) => void;
    };

/** Show the recording as soon as it loads, while the service replay runs in parallel. */
export function useAnalysisReplay(fixture: string | undefined): AnalysisReplayController {
  const [loaded, setLoaded] = useState<ReplayLoadState>({ status: 'idle' });
  const [frameIndex, setFrameIndex] = useState(-1);
  const [playing, setPlaying] = useState(false);
  const [framesPerSecond, setFramesPerSecond] = useState<1 | 4 | 16>(1);
  const currentPresentation = useRef<ReplayPresentation | null>(null);

  const matches = fixture !== undefined && 'fixture' in loaded && loaded.fixture === fixture;
  const ready = matches && loaded.status === 'ready' ? loaded.presentation : null;

  useEffect(() => {
    if (!fixture) {
      setLoaded({ status: 'idle' });
      setFrameIndex(-1);
      setPlaying(false);
      currentPresentation.current = null;
      return;
    }

    let cancelled = false;
    let recorded: ReplayPresentation | null = null;
    let serviceResponse: AnalysisReplayRun | null = null;

    const publish = (presentation: ReplayPresentation) => {
      currentPresentation.current = presentation;
      setLoaded({ status: 'ready', fixture, presentation });
      setFrameIndex(presentation.frameIndex);
    };

    setLoaded({ status: 'loading', fixture });
    setFrameIndex(-1);
    setPlaying(false);
    currentPresentation.current = null;

    // The local recording fetch and the service request start together. The
    // recording supplies working controls while a slow replay is computed.
    void loadFixtureReplay(fixture)
      .then((frames) => {
        if (cancelled) return;
        recorded = recordedPresentation(frames);
        const current = currentPresentation.current;
        if (current?.source === 'live') return;
        const visible = serviceResponse?.source === 'live'
          ? upgradePresentation(recorded, serviceResponse)
          : recorded;
        publish(visible);
      })
      .catch(() => {
        if (!cancelled && !serviceResponse) setLoaded({ status: 'failed', fixture });
      });

    void replayFixture(fixture)
      .then((service) => {
        if (cancelled) return;
        serviceResponse = service;

        if (recorded) {
          if (service.source !== 'live') return;
          const current = currentPresentation.current ?? recorded;
          publish(upgradePresentation(current, service));
          return;
        }

        // The adapter also carries the committed frames if the local recording
        // endpoint failed before returning its copy.
        if (service.source === 'fixture') {
          recorded = recordedPresentation(service.frames);
          publish(recorded);
          return;
        }

        const current = currentPresentation.current ?? recordedPresentation(service.frames);
        publish(upgradePresentation(current, service));
      })
      .catch(() => {
        // A service failure leaves any recording already shown untouched.
        if (!cancelled && !recorded) setLoaded({ status: 'failed', fixture });
      });

    return () => {
      cancelled = true;
    };
  }, [fixture]);

  const safeIndex = ready
    ? Math.max(0, Math.min(ready.frames.length - 1, frameIndex < 0 ? ready.frames.length - 1 : frameIndex))
    : 0;

  useEffect(() => {
    if (!playing || !ready) return;
    if (safeIndex >= ready.frames.length - 1) {
      setPlaying(false);
      return;
    }
    const timer = window.setTimeout(() => {
      const nextIndex = safeIndex + 1;
      currentPresentation.current = { ...ready, frameIndex: nextIndex };
      setFrameIndex(nextIndex);
    }, 1000 / framesPerSecond);
    return () => window.clearTimeout(timer);
  }, [frameIndex, framesPerSecond, playing, ready, safeIndex]);

  const selectFrame = useCallback(
    (index: number) => {
      if (!ready) return;
      const nextIndex = Math.max(0, Math.min(ready.frames.length - 1, index));
      currentPresentation.current = { ...ready, frameIndex: nextIndex };
      setPlaying(false);
      setFrameIndex(nextIndex);
    },
    [ready],
  );

  const togglePlayback = useCallback(() => {
    if (!ready) return;
    if (playing) {
      setPlaying(false);
      return;
    }
    if (safeIndex >= ready.frames.length - 1) {
      currentPresentation.current = { ...ready, frameIndex: 0 };
      setFrameIndex(0);
    }
    setPlaying(true);
  }, [playing, ready, safeIndex]);

  if (!fixture) return { status: 'idle' };
  if (loaded.status === 'failed' && matches) return { status: 'failed' };
  if (!ready) return { status: 'loading' };

  const frame = ready.frames[safeIndex];
  if (!frame) return { status: 'failed' };

  return {
    status: 'ready',
    frames: ready.frames,
    source: ready.source,
    frameIndex: safeIndex,
    frame,
    playing,
    framesPerSecond,
    selectFrame,
    togglePlayback,
    setFramesPerSecond,
  };
}
